import { useCallback, useEffect, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    $getSelection,
    $isRangeSelection,
    $isParagraphNode,
    $isRootOrShadowRoot,
    FORMAT_TEXT_COMMAND,
    UNDO_COMMAND,
    REDO_COMMAND,
} from 'lexical';
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND, $isListNode, ListNode } from '@lexical/list';
import { mergeRegister, $getNearestNodeOfType, $findMatchingParent } from '@lexical/utils';
import { ArrowUturnLeftIcon, ArrowUturnRightIcon, ListBulletIcon } from '@heroicons/react/24/outline';
import { ReactComponent as NumberedList } from '../../../assets/numbered-list.svg';

function ToolbarPlugin(): JSX.Element {
    const [editor] = useLexicalComposerContext();
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [blockType, setBlockType] = useState<string>('paragraph');

    const style = `w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors duration-100 ease-in`;

    const buttons = [
        {
            style: style,
            onClick: (): void => {
                editor.dispatchCommand(UNDO_COMMAND, undefined);
            },
            icon: <ArrowUturnLeftIcon className="w-4 h-4" />,
        },
        {
            style: style,
            onClick: (): void => {
                editor.dispatchCommand(REDO_COMMAND, undefined);
            },
            icon: <ArrowUturnRightIcon className="w-4 h-4" />,
        },
        {
            style: `${style} ${isBold && 'bg-gray-100'}`,
            onClick: (): void => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
            },
            icon: <p className="font-bold">B</p>,
        },
        {
            style: `${style} ${isItalic && 'bg-gray-100'}`,
            onClick: (): void => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
            },
            icon: <p className="italic font-serif">I</p>,
        },
        {
            style: `${style} ${isUnderline && 'bg-gray-100'}`,
            onClick: (): void => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
            },
            icon: <p className="underline">U</p>,
        },
        {
            style: `${style} ${blockType === 'bullet' && 'bg-gray-100'}`,
            onClick: (): void => {
                formatList('bullet');
            },
            icon: <ListBulletIcon className="w-5 h-5" />,
        },
        {
            style: `${style} ${blockType === 'number' && 'bg-gray-100'}`,
            onClick: (): void => {
                formatList('number');
            },
            icon: <NumberedList className="w-5 h-5" />,
        },
    ];

    const updateToolbar = useCallback(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();
            let element =
                anchorNode.getKey() === 'root'
                    ? anchorNode
                    : $findMatchingParent(anchorNode, (e) => {
                          const parent = e.getParent();
                          return parent !== null && $isRootOrShadowRoot(parent);
                      });

            if (element === null) {
                element = anchorNode.getTopLevelElementOrThrow();
            }

            const elementKey = element.getKey();
            const elementDOM = editor.getElementByKey(elementKey);

            // Update text format
            setIsBold(selection.hasFormat('bold'));
            setIsItalic(selection.hasFormat('italic'));
            setIsUnderline(selection.hasFormat('underline'));

            // Update list format
            if (elementDOM !== null) {
                if ($isListNode(element)) {
                    const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
                    const type = parentList ? parentList.getListType() : element.getListType();
                    setBlockType(type);
                } else if ($isParagraphNode(element)) {
                    setBlockType('paragraph');
                }
            }
        }
    }, [editor]);

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({ editorState }) => {
                editorState.read(() => {
                    updateToolbar();
                });
            })
        );
    }, [updateToolbar, editor]);

    function formatList(listType: string): void {
        if (listType === 'number' && blockType !== 'number') {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
            setBlockType('number');
        } else if (listType === 'bullet' && blockType !== 'bullet') {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
            setBlockType('bullet');
        } else {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
            setBlockType('paragraph');
        }
    }

    return (
        <div className="p-0.5 flex items-center gap-0.5 border-b">
            {buttons.map((b, idx) => {
                return (
                    <button key={idx} type="button" className={b.style} onClick={b.onClick}>
                        {b.icon}
                    </button>
                );
            })}
        </div>
    );
}

export default ToolbarPlugin;
